import { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { ClipboardCheck, ChevronDown, ChevronUp } from "lucide-react";
import PageHeader from "../components/PageHeader";

export default function AuditsPage() {
  const { axiosAuth } = useAuth();
  const api = axiosAuth();
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    api.get("/audits").then((r) => { setAudits(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const statusColors = {
    en_cours: "bg-blue-500/10 text-blue-400 border-blue-500/20",
    termine: "bg-green-500/10 text-green-400 border-green-500/20",
    en_attente: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20",
  };
  const statusLabels = { en_cours: "En cours", termine: "Terminé", en_attente: "En attente" };

  if (loading) return <div className="p-8 text-slate-400">Chargement...</div>;

  return (
    <div className="p-8 max-w-7xl" data-testid="audits-page">
      <PageHeader
        icon={<ClipboardCheck />}
        title="Mes Audits"
        description="Rapports d'audit et recommandations"
        testId="audits-header"
      />

      {audits.length === 0 ? (
        <div className="bg-[#14101c] border border-white/10 rounded-xl p-12 text-center">
          <ClipboardCheck className="w-12 h-12 mx-auto mb-4 text-slate-600" />
          <h3 className="text-xl font-bold mb-2">Aucun audit</h3>
          <p className="text-slate-500">Vos rapports d'audit apparaîtront ici.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {audits.map((audit, i) => (
            <div
              key={audit.audit_id}
              className="bg-[#14101c] border border-white/10 rounded-xl overflow-hidden hover:border-[#f7c935]/30 transition-colors animate-fade-in-up"
              style={{ animationDelay: `${i * 0.06}s` }}
              data-testid={`audit-card-${audit.audit_id}`}
            >
              <button
                onClick={() => setExpanded(expanded === audit.audit_id ? null : audit.audit_id)}
                className="w-full p-6 flex items-center justify-between text-left"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: "#461d8a20" }}>
                    <ClipboardCheck className="w-5 h-5" style={{ color: "#5a2d9c" }} />
                  </div>
                  <div>
                    <h3 className="font-bold">{audit.title}</h3>
                    <div className="text-sm text-slate-500 mt-0.5">{new Date(audit.created_at).toLocaleDateString("fr-FR")}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-xs px-2 py-1 rounded-full font-medium border ${statusColors[audit.status] || statusColors.en_cours}`}>
                    {statusLabels[audit.status] || audit.status}
                  </span>
                  {expanded === audit.audit_id ? <ChevronUp className="w-5 h-5 text-slate-500" /> : <ChevronDown className="w-5 h-5 text-slate-500" />}
                </div>
              </button>
              {expanded === audit.audit_id && (
                <div className="px-6 pb-6 border-t border-white/5 pt-4">
                  <div className="text-sm text-slate-300 whitespace-pre-wrap mb-4">{audit.content}</div>
                  {audit.recommendations?.length > 0 && (
                    <div>
                      <h4 className="text-sm font-bold mb-2" style={{ color: "#f7c935" }}>Recommandations</h4>
                      <ul className="space-y-2">
                        {audit.recommendations.map((rec, j) => (
                          <li key={j} className="flex items-start gap-2 text-sm text-slate-400">
                            <span className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0" style={{ background: "#f7c935" }} />
                            {rec}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
